
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import MobileBottomNav from '../../components/MobileBottomNav';

const safeTrim = (v) => (typeof v === 'string' ? v.trim() : '');
const isNonEmpty = (v) => safeTrim(v).length > 0;

const AddCourseLecturerPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);


  const [activeTab, setActiveTab] = useState('lecturer');


  const [lecturerForm, setLecturerForm] = useState({
    name: '',
    email: '',
    college: '',
    department: '',
    position: '',
  });


  const [courseForm, setCourseForm] = useState({
    name: '',
    code: '',
    college: '',
    department: '',
  });


  const [lecturerSuccess, setLecturerSuccess] = useState('');
  const [lecturerError, setLecturerError] = useState('');
  const [lecturerLoading, setLecturerLoading] = useState(false);


  const [courseInfo, setCourseInfo] = useState('');
  const [courseError, setCourseError] = useState('');
  const [courseLoading, setCourseLoading] = useState(false);


  const [savedCourses, setSavedCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);


  const [doctorMetaLoading, setDoctorMetaLoading] = useState(false);
  const [doctorMetaError, setDoctorMetaError] = useState('');
  const [colleges, setColleges] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [departmentsByCollege, setDepartmentsByCollege] = useState({});


  const [lecturerRequests, setLecturerRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState('');
  const [editingRequestId, setEditingRequestId] = useState(null);


  const [reqActionLoading, setReqActionLoading] = useState({});


  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const apiFetch = useCallback(async (url, options = {}) => {
    const headers = {
      ...(options.headers || {}),
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    };

    const resp = await fetch(url, { ...options, credentials: 'include', headers });


    const text = await resp.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = text || null;
    }

    if (!resp.ok) {
      const msg =
        (data && typeof data === 'object' && (data.message || data.error)) ||
        (typeof data === 'string' && data) ||
        `HTTP ${resp.status}`;
      const err = new Error(String(msg));
      err.status = resp.status;
      err.data = data;
      throw err;
    }

    return data;
  }, []);


  const loadCourses = useCallback(async () => {
    setCoursesLoading(true);
    try {
      const data = await apiFetch('/api/courses', { method: 'GET' });
      if (Array.isArray(data)) setSavedCourses(data);
      else if (data && Array.isArray(data.courses)) setSavedCourses(data.courses);
      else setSavedCourses([]);
    } catch (err) {
      console.error('Error loading courses:', err);

    } finally {
      setCoursesLoading(false);
    }
  }, [apiFetch]);


  const loadDoctorMeta = useCallback(async () => {
    setDoctorMetaError('');
    setDoctorMetaLoading(true);
    try {
      const data = await apiFetch('/api/doctor-meta', { method: 'GET' });
      setColleges(Array.isArray(data?.colleges) ? data.colleges : []);
      setAllDepartments(Array.isArray(data?.departments) ? data.departments : []);
      setDepartmentsByCollege(
        data?.departmentsByCollege && typeof data.departmentsByCollege === 'object'
          ? data.departmentsByCollege
          : {}
      );
    } catch (err) {
      console.error('Error loading doctor meta:', err);
      setDoctorMetaError('تعذر تحميل بيانات الكليات والأقسام.');
    } finally {
      setDoctorMetaLoading(false);
    }
  }, [apiFetch]);


  const loadLecturerRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsError('');
    try {
      const data = await apiFetch('/api/lecturer-requests', { method: 'GET' });

      if (Array.isArray(data?.requests)) setLecturerRequests(data.requests);
      else if (Array.isArray(data)) setLecturerRequests(data);
      else setLecturerRequests([]);
    } catch (err) {
      console.error('Error loading lecturer requests:', err);
      setLecturerRequests([]);
      setRequestsError('تعذر تحميل طلبات إضافة المحاضرين.');
    } finally {
      setRequestsLoading(false);
    }
  }, [apiFetch]);


  useEffect(() => {
    loadCourses();
    loadDoctorMeta();
    loadLecturerRequests();
  }, [loadCourses, loadDoctorMeta, loadLecturerRequests]);


  const lecturerDepartmentsOptions = useMemo(() => {
    const key = safeTrim(lecturerForm.college);
    const list = key && Array.isArray(departmentsByCollege[key]) ? departmentsByCollege[key] : [];
    return list.length ? list : allDepartments;
  }, [lecturerForm.college, departmentsByCollege, allDepartments]);

  const courseDepartmentsOptions = useMemo(() => {
    const key = safeTrim(courseForm.college);
    const list = key && Array.isArray(departmentsByCollege[key]) ? departmentsByCollege[key] : [];
    return list.length ? list : allDepartments;
  }, [courseForm.college, departmentsByCollege, allDepartments]);

  const handleLecturerInputChange = (field, value) => {
    setLecturerForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'college' ? { department: '' } : {}),
    }));
  };

  const handleCourseInputChange = (field, value) => {
    setCourseForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'college' ? { department: '' } : {}),
    }));
  };


  const updateRequestStatus = useCallback(
    async (id, status) => {
      if (!id) throw new Error('Missing request id');

      const payload = { id, status };
      const data = await apiFetch('/api/lecturer-requests/status', {
        method: 'POST',
        body: JSON.stringify(payload),
      });


      const updated = data?.request || data;


      if (updated && updated.id) {
        setLecturerRequests((prev) =>
          prev.map((r) => (r.id === updated.id ? updated : r))
        );
      } else {
        await loadLecturerRequests();
      }

      return updated;
    },
    [apiFetch, loadLecturerRequests]
  );

  const setReqLoading = (id, key, val) => {
    if (!id) return;
    setReqActionLoading((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: val },
    }));
  };


  const handleAcceptRequest = useCallback(
    async (req) => {
      const id = req?.id;
      try {
        setRequestsError('');
        setLecturerSuccess('');
        setLecturerError('');

        if (!id) {
          setRequestsError('لا يمكن قبول الطلب: رقم الطلب غير موجود.');
          return;
        }

        const name = safeTrim(req?.lecturer_name);
        const email = safeTrim(req?.email);

        if (!name) {
          setRequestsError('لا يمكن قبول الطلب: اسم المحاضر غير موجود.');
          return;
        }

        setReqLoading(id, 'accept', true);


        await apiFetch('/api/lecturers', {
          method: 'POST',
          body: JSON.stringify({
            name,
            email,
            college: req?.college_name || '',
            department: req?.specialization || '',
            position: 'محاضر',
          }),
        });

        await updateRequestStatus(id, 'accepted');

        setLecturerSuccess('تم قبول الطلب وإضافة المحاضر إلى قاعدة البيانات.');

        await loadLecturerRequests();
      } catch (err) {
        console.error('Error accepting request:', err);
        setRequestsError('حدث خطأ أثناء قبول الطلب. تأكد من الخادم وحاول مرة أخرى.');
      } finally {
        if (id) setReqLoading(id, 'accept', false);
      }
    },
    [apiFetch, updateRequestStatus, loadLecturerRequests]
  );


  const handleRejectRequest = useCallback(
    async (req) => {
      const id = req?.id;
      if (!id) {
        setRequestsError('لا يمكن رفض الطلب: رقم الطلب غير موجود.');
        return;
      }
      const ok = window.confirm('هل أنت متأكد من رفض هذا الطلب؟');
      if (!ok) return;

      try {
        setRequestsError('');
        setReqLoading(id, 'reject', true);
        await updateRequestStatus(id, 'rejected');
        await loadLecturerRequests();
      } catch (err) {
        console.error('Error rejecting request:', err);
        setRequestsError('تعذر رفض الطلب، حاول مرة أخرى.');
      } finally {
        setReqLoading(id, 'reject', false);
      }
    },
    [updateRequestStatus, loadLecturerRequests]
  );


  const handleEditRequest = (req) => {
    const id = req?.id;
    if (!id) {
      setRequestsError('لا يمكن تعديل الطلب: رقم الطلب غير موجود.');
      return;
    }

    setActiveTab('lecturer');
    setEditingRequestId(id);
    setLecturerSuccess('');
    setLecturerError('');

    setLecturerForm({
      name: req?.lecturer_name || '',
      email: req?.email || '',
      college: req?.college_name || '',
      department: req?.specialization || '',
      position: 'محاضر',
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEditing = () => {
    setEditingRequestId(null);
    setLecturerSuccess('');
    setLecturerError('');
    setLecturerForm({
      name: '',
      email: '',
      college: '',
      department: '',
      position: '',
    });
  };


  const handleLecturerSubmit = async (e) => {
    e.preventDefault();
    setLecturerSuccess('');
    setLecturerError('');
    setRequestsError('');

    const name = safeTrim(lecturerForm.name);
    const email = safeTrim(lecturerForm.email);
    const position = safeTrim(lecturerForm.position);

    if (!name) {
      setLecturerError('اسم المحاضر مطلوب.');
      return;
    }
    if (!email) {
      setLecturerError('البريد الإلكتروني مطلوب.');
      return;
    }
    if (!position) {
      setLecturerError('يجب اختيار المسمى الوظيفي للمحاضر.');
      return;
    }

    try {
      setLecturerLoading(true);

      const payload = {
        ...lecturerForm,
        name,
        email,
        position,
      };

      await apiFetch('/api/lecturers', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (editingRequestId) {

        try {
          await updateRequestStatus(editingRequestId, 'accepted');
          setLecturerSuccess('تم إضافة المحاضر وتحديث حالة الطلب إلى مقبول.');
          setEditingRequestId(null);
          await loadLecturerRequests();
        } catch (err) {
          console.error('Error updating request status after lecturer save:', err);
          setLecturerSuccess('تم إضافة المحاضر، لكن تعذر تحديث حالة الطلب (تحقق من الخادم).');
        }
      } else {
        setLecturerSuccess('تم إضافة المحاضر بنجاح إلى قاعدة البيانات.');
      }

      setLecturerForm({
        name: '',
        email: '',
        college: '',
        department: '',
        position: '',
      });
    } catch (err) {
      console.error('Error adding lecturer:', err);
      setLecturerError('حدث خطأ أثناء إضافة المحاضر. تأكد من الخادم وحاول مرة أخرى.');
    } finally {
      setLecturerLoading(false);
    }
  };


  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    setCourseInfo('');
    setCourseError('');

    const name = safeTrim(courseForm.name);
    const code = safeTrim(courseForm.code);

    if (!name) {
      setCourseError('اسم المقرر مطلوب.');
      return;
    }

    try {
      setCourseLoading(true);

      await apiFetch('/api/courses', {
        method: 'POST',
        body: JSON.stringify({ ...courseForm, name, code }),
      });

      setCourseForm({
        name: '',
        code: '',
        college: '',
        department: '',
      });

      setCourseInfo('تم إضافة المقرر بنجاح إلى قاعدة البيانات.');
      await loadCourses();
    } catch (err) {
      console.error('Error adding course:', err);
      setCourseError('حدث خطأ أثناء إضافة المقرر. تأكد من الخادم وحاول مرة أخرى.');
    } finally {
      setCourseLoading(false);
    }
  };

  const formCardStyle = {
    borderRadius: 20,
    border: '1px solid rgba(40,227,218,.22)',
    background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
    boxShadow: '0 8px 32px rgba(0,0,0,.35)',
    backdropFilter: 'blur(6px)',
    padding: '18px 18px 22px',
  };

  const toggleButtonStyle = (isActive) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    borderRadius: 14,
    fontFamily: 'Alexandria',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    border: isActive
      ? '1px solid rgba(8,20,35,.6)'
      : '1px solid rgba(211,216,218,0.7)',
    background: isActive
      ? 'linear-gradient(135deg,#28E3DA,#7DF9E8)'
      : 'rgba(213,210,210,0.15)',
    color: isActive ? '#081423' : '#D5D2D2',
    cursor: 'pointer',
    minWidth: 120,
  });

  const inputStyle = {
    width: '100%',
    height: '42.75px',
    background: '#12334A',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.08)',
    outline: 'none',
    color: '#FFFFFF',
    padding: '0 16px',
    fontSize: '14px',
    fontFamily: 'Alexandria',
    direction: 'rtl',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
  };

  const statusBadge = (status) => {
    const s = status || 'pending';
    let label = '';
    let bg = '';
    let border = '';

    if (s === 'accepted') {
      label = 'مقبول';
      bg = 'rgba(16,185,129,0.16)';
      border = 'rgba(16,185,129,0.7)';
    } else if (s === 'rejected') {
      label = 'مرفوض';
      bg = 'rgba(239,68,68,0.16)';
      border = 'rgba(239,68,68,0.7)';
    } else {
      label = 'قيد المراجعة';
      bg = 'rgba(56,189,248,0.16)';
      border = 'rgba(56,189,248,0.7)';
    }

    return (
      <span
        style={{
          fontSize: 11,
          padding: '2px 8px',
          borderRadius: 999,
          border: `1px solid ${border}`,
          background: bg,
          color: '#E5F3FF',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  };

  const pendingRequests = useMemo(
    () => lecturerRequests.filter((r) => (r?.status || 'pending') === 'pending'),
    [lecturerRequests]
  );
  const resolvedRequests = useMemo(
    () => lecturerRequests.filter((r) => (r?.status || 'pending') !== 'pending'),
    [lecturerRequests]
  );

  return (
    <div
      className="min-h-screen text-white bg-transparent"
      style={{
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'row',
        background:
          'linear-gradient(0deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40,227,218,0.16) 0%, rgba(40,227,218,0) 100%), #081423',
      }}
    >
      {                     }
      <div className="hidden md:block sticky top-0 h-screen z-[200]">
        <Sidebar isOpen={isSidebarOpen} onClose={setIsSidebarOpen} />
      </div>

      {                    }
      <div className="md:hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={setIsSidebarOpen} />
      </div>

      {                  }
      <div
        className="flex-1 flex flex-col md:ml-0"
        style={{ width: '100vw', marginLeft: isMobile ? '0' : 'auto' }}
      >
        <div
          className="flex-1 p-6 pb-20 md:pb-6"
          style={{
            maxWidth: '100%',
            padding: isMobile ? '16px 12px 80px' : isTablet ? '20px 24px 24px' : '24px 48px 32px',
          }}
        >
          {             }
          <nav style={{
            display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'space-evenly',
            flexWrap: 'wrap', gap: isMobile ? '6px' : '4px',
            padding: isMobile ? '10px 12px' : '0 16px',
            height: isMobile ? 'auto' : '52px',
            background: 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,.2) 0%,rgba(40,227,218,0) 100%),rgba(9,28,43,.6)',
            border: '1px solid rgba(40,227,218,.2)',
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
            marginBottom: '24px',
          }}>
            {[
              { label: 'نظرة عامة',           path: '/dashboard' },
              { label: 'إدارة المستخدمين',    path: '/user-management' },
              { label: 'سجل النشاط',          path: '/activity-log' },
              { label: 'الإبلاغات',           path: '/reports' },
              { label: 'إضافة مقرر / محاضر', active: true },
              { label: 'إدارة المقررات',      path: '/manage-courses-lecturers' },
            ].map((item, i) => (
              <React.Fragment key={item.label}>
                {i > 0 && !isMobile && <span style={{ color: 'rgba(255,255,255,.15)', fontSize: '18px', userSelect: 'none' }}>|</span>}
                <span
                  onClick={() => item.path && navigate(item.path)}
                  style={{
                    fontFamily: 'Alexandria,sans-serif', fontSize: isMobile ? '12px' : '14px',
                    fontWeight: item.active ? 800 : 500, color: item.active ? '#7DF9E8' : '#94a3b8',
                    cursor: item.path ? 'pointer' : 'default',
                    padding: isMobile ? '5px 10px' : '0 4px', borderRadius: isMobile ? '8px' : 0,
                    background: item.active && isMobile ? 'rgba(40,227,218,.1)' : 'transparent',
                    transition: 'color .15s', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (item.path) e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { if (item.path) e.currentTarget.style.color = '#94a3b8'; }}
                >{item.label}</span>
              </React.Fragment>
            ))}
          </nav>

          {                      }
          <div className="flex justify-center mb-8">
            <div
              className="inline-flex items-center gap-2 rounded-[18px] px-2 py-2"
              style={{
                direction: 'rtl',
                border: '1px solid rgba(40,227,218,.22)',
                background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <button type="button" onClick={() => setActiveTab('lecturer')} style={toggleButtonStyle(activeTab === 'lecturer')}>
                إضافة محاضر
              </button>
              <button type="button" onClick={() => setActiveTab('course')} style={toggleButtonStyle(activeTab === 'course')}>
                إضافة مقرر
              </button>
            </div>
          </div>

          {doctorMetaError && (
            <div className="max-w-2xl mx-auto mb-4 rounded-xl border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-100 text-right">
              {doctorMetaError}
            </div>
          )}

          {                     }
          <div className="px-4">
            {activeTab === 'lecturer' && (
              <div className="w-full max-w-2xl mx-auto" style={formCardStyle}>
                <div className="mb-6 flex flex-col items-end" style={{ direction: 'rtl' }}>
                  <h2 style={{ fontFamily: 'Alexandria', fontWeight: 700, fontSize: '13px', marginBottom: 6 }}>
                    {editingRequestId ? 'تعديل طلب إضافة محاضر' : 'إضافة محاضر جديد'}
                  </h2>
                  <p
                    style={{
                      fontFamily: 'Alexandria',
                      fontWeight: 500,
                      fontSize: '11px',
                      color: '#D5D2D2',
                      maxWidth: '420px',
                      textAlign: 'right',
                    }}
                  >
                    {editingRequestId
                      ? 'أنت الآن تعدّل بيانات محاضر قادم من طلب أحد المستخدمين. بعد الحفظ سيتم قبول الطلب وتسجيل المحاضر في القاعدة الأساسية.'
                      : 'يمكنك تسجيل محاضر جديد ليتم استخدامه في التقييمات والواجهات الأخرى. سيتم حفظ البيانات في جدول المحاضرين الأساسي.'}
                  </p>

                  {editingRequestId && (
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="mt-3 text-[11px] text-slate-300 hover:text-white underline"
                      style={{ fontFamily: 'Alexandria' }}
                    >
                      إلغاء وضع التعديل
                    </button>
                  )}
                </div>

                {lecturerSuccess && (
                  <div className="mb-4 rounded-xl border border-emerald-500/60 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-100 text-right">
                    {lecturerSuccess}
                  </div>
                )}
                {lecturerError && (
                  <div className="mb-4 rounded-xl border border-rose-500/60 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-100 text-right">
                    {lecturerError}
                  </div>
                )}

                <form onSubmit={handleLecturerSubmit} className="space-y-5" style={{ direction: 'rtl' }}>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-right">اسم المحاضر</label>
                    <input
                      type="text"
                      value={lecturerForm.name}
                      onChange={(e) => handleLecturerInputChange('name', e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-right">البريد الإلكتروني</label>
                    <input
                      type="email"
                      value={lecturerForm.email}
                      onChange={(e) => handleLecturerInputChange('email', e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-right">اسم الكلية</label>
                      <select
                        value={lecturerForm.college}
                        onChange={(e) => handleLecturerInputChange('college', e.target.value)}
                        style={selectStyle}
                        disabled={doctorMetaLoading}
                      >
                        <option value="">{doctorMetaLoading ? 'جاري التحميل...' : 'اختر الكلية'}</option>
                        {colleges.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-right">القسم</label>
                      <select
                        value={lecturerForm.department}
                        onChange={(e) => handleLecturerInputChange('department', e.target.value)}
                        style={selectStyle}
                        disabled={doctorMetaLoading}
                      >
                        <option value="">{doctorMetaLoading ? 'جاري التحميل...' : 'اختر القسم'}</option>
                        {lecturerDepartmentsOptions.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-right">المسمى الوظيفي</label>
                    <select
                      value={lecturerForm.position}
                      onChange={(e) => handleLecturerInputChange('position', e.target.value)}
                      style={selectStyle}
                      required
                    >
                      <option value="">اختر المسمى الوظيفي</option>
                      <option value="محاضر">محاضر</option>
                      <option value="مدرس">مدرس</option>
                      <option value="أستاذ">أستاذ</option>
                      <option value="معيد">معيد</option>
                      <option value="أستاذ مشارك">أستاذ مشارك</option>
                      <option value="أستاذ مساعد">أستاذ مساعد</option>
                    </select>
                  </div>

                  <div className="flex justify-center mt-4">
                    <button
                      type="submit"
                      disabled={lecturerLoading}
                      className="px-6 py-2 rounded-[14px] text-sm font-bold"
                      style={{
                        border: '0.75px solid rgba(40,227,218,0.6)',
                        background: lecturerLoading ? 'rgba(40,227,218,0.25)' : 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
                        color: '#081423',
                        boxShadow: '0 0 18px rgba(40,227,218,.55)',
                        opacity: lecturerLoading ? 0.8 : 1,
                      }}
                    >
                      {lecturerLoading ? 'جاري الإضافة...' : editingRequestId ? 'حفظ وقبول الطلب' : 'إضافة'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'course' && (
              <div className="w-full max-w-2xl mx-auto space-y-6">
                <div style={formCardStyle}>
                  <div className="mb-6 flex flex-col items-end" style={{ direction: 'rtl' }}>
                    <h2 style={{ fontFamily: 'Alexandria', fontWeight: 700, fontSize: '13px', marginBottom: 6 }}>
                      أضف بيانات المقرر
                    </h2>
                    <p
                      style={{
                        fontFamily: 'Alexandria',
                        fontWeight: 500,
                        fontSize: '11px',
                        color: '#D5D2D2',
                        maxWidth: '420px',
                        textAlign: 'right',
                      }}
                    >
                      سيتم حفظ المقرر في قاعدة بيانات المقررات لاستخدامه في التقارير والتقييمات لاحقًا.
                    </p>
                  </div>

                  {courseInfo && (
                    <div className="mb-4 rounded-xl border border-cyan-500/60 bg-cyan-500/10 px-3 py-2 text-[12px] text-cyan-100 text-right">
                      {courseInfo}
                    </div>
                  )}
                  {courseError && (
                    <div className="mb-4 rounded-xl border border-rose-500/60 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-100 text-right">
                      {courseError}
                    </div>
                  )}

                  <form onSubmit={handleCourseSubmit} className="space-y-5" style={{ direction: 'rtl' }}>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-right">اسم المقرر</label>
                      <input
                        type="text"
                        value={courseForm.name}
                        onChange={(e) => handleCourseInputChange('name', e.target.value)}
                        style={inputStyle}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-right">رمز المقرر</label>
                      <input
                        type="text"
                        value={courseForm.code}
                        onChange={(e) => handleCourseInputChange('code', e.target.value)}
                        style={inputStyle}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-right">اسم الكلية</label>
                        <select
                          value={courseForm.college}
                          onChange={(e) => handleCourseInputChange('college', e.target.value)}
                          style={selectStyle}
                          disabled={doctorMetaLoading}
                        >
                          <option value="">{doctorMetaLoading ? 'جاري التحميل...' : 'اختر الكلية'}</option>
                          {colleges.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-right">القسم</label>
                        <select
                          value={courseForm.department}
                          onChange={(e) => handleCourseInputChange('department', e.target.value)}
                          style={selectStyle}
                          disabled={doctorMetaLoading}
                        >
                          <option value="">{doctorMetaLoading ? 'جاري التحميل...' : 'اختر القسم'}</option>
                          {courseDepartmentsOptions.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-center mt-4">
                      <button
                        type="submit"
                        disabled={courseLoading}
                        className="px-6 py-2 rounded-[14px] text-sm font-bold"
                        style={{
                          border: '0.75px solid rgba(40,227,218,0.6)',
                          background: courseLoading ? 'rgba(40,227,218,0.25)' : 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
                          color: '#081423',
                          boxShadow: '0 0 18px rgba(40,227,218,.55)',
                          opacity: courseLoading ? 0.85 : 1,
                        }}
                      >
                        {courseLoading ? 'جاري الإضافة...' : 'إضافة'}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="w-full max-w-2xl mx-auto" style={formCardStyle}>
                  <div className="mb-4 flex justify-between items-center" style={{ direction: 'rtl' }}>
                    <span style={{ fontFamily: 'Alexandria', fontSize: 14, fontWeight: 600 }}>
                      المقررات المسجلة في النظام
                    </span>
                    <span className="text-[12px] text-slate-300" style={{ direction: 'rtl' }}>
                      {coursesLoading ? 'جاري التحميل...' : `العدد: ${savedCourses.length}`}
                    </span>
                  </div>

                  {coursesLoading && (
                    <div className="text-[12px] text-slate-300 text-right">جاري تحميل المقررات...</div>
                  )}

                  {!coursesLoading && savedCourses.length === 0 && (
                    <div className="text-[12px] text-slate-300 text-right">لا توجد مقررات محفوظة حتى الآن.</div>
                  )}

                  {!coursesLoading && savedCourses.length > 0 && (
                    <div className="space-y-3" style={{ direction: 'rtl' }}>
                      {savedCourses.map((c) => (
                        <div
                          key={c.id || `${c.name}-${c.code}`}
                          className="px-3 py-3 text-[12px]"
                          style={{ background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.10) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)', border: '1px solid rgba(40,227,218,.22)', borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,.25)' }}
                        >
                          <div className="flex justify-between mb-1">
                            <span className="text-white font-semibold">{c.name || '—'}</span>
                            <span className="text-[#B8C5CE]">{c.code || 'بدون رمز'}</span>
                          </div>
                          <div className="text-[#B8C5CE] mb-1">الكلية: {c.college || 'غير محددة'}</div>
                          <div className="text-[#B8C5CE]">القسم: {c.department || 'غير محدد'}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 text-[11px] text-emerald-200 text-right">
                    * القائمة تعرض المقررات المخزنة في قاعدة البيانات.
                  </div>
                </div>
              </div>
            )}
          </div>

          {                           }
          <div className="mt-10 px-4">
            <div className="w-full max-w-3xl mx-auto" style={formCardStyle}>
              <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2" style={{ direction: 'rtl' }}>
                <div>
                  <h3 style={{ fontFamily: 'Alexandria', fontWeight: 700, fontSize: '14px' }}>
                    طلبات إضافة المحاضرين
                  </h3>
                  <p style={{ fontFamily: 'Alexandria', fontWeight: 500, fontSize: '11px', color: '#D5D2D2' }}>
                    الطلبات القادمة من المستخدمين. يمكنك قبولها، تعديلها قبل القبول، أو رفضها.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-[11px] text-slate-300">
                    {requestsLoading ? 'جاري تحميل الطلبات...' : `الإجمالي: ${lecturerRequests.length}`}
                  </div>
                  <button
                    type="button"
                    onClick={loadLecturerRequests}
                    disabled={requestsLoading}
                    className="text-[11px] px-3 py-1 rounded-full border border-white/15 hover:bg-white/5 disabled:opacity-60"
                    style={{ fontFamily: 'Alexandria' }}
                  >
                    تحديث
                  </button>
                </div>
              </div>

              {requestsError && (
                <div className="mb-4 rounded-xl border border-rose-500/60 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-100 text-right">
                  {requestsError}
                </div>
              )}

              {!requestsLoading && lecturerRequests.length === 0 && (
                <div className="text-[12px] text-slate-300 text-right">لا توجد طلبات مسجلة حتى الآن.</div>
              )}

              {pendingRequests.length > 0 && (
                <div className="mb-5" style={{ direction: 'rtl' }}>
                  <div className="mb-2 text-[12px] text-cyan-100 font-medium text-right">طلبات قيد المراجعة</div>
                  <div className="space-y-3">
                    {pendingRequests.map((req) => {
                      const id = req?.id;
                      const loadingAccept = id && reqActionLoading[id]?.accept;
                      const loadingReject = id && reqActionLoading[id]?.reject;

                      return (
                        <div
                          key={id || `${req.lecturer_name}-${req.email}`}
                          className="px-3 py-3 text-[12px]"
                          style={{ background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.10) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)', border: '1px solid rgba(56,189,248,.45)', borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,.25)' }}
                        >
                          <div className="flex justify-between items-center mb-1 gap-2">
                            <div className="flex items-center gap-3">
                              {req.image_data && (
                                <img
                                  src={req.image_data}
                                  alt={req.lecturer_name || 'صورة المحاضر'}
                                  className="w-9 h-9 rounded-full object-cover border border-[rgba(148,163,184,.9)]"
                                />
                              )}
                              <div className="flex flex-col items-end">
                                <span className="text-white font-semibold">{req.lecturer_name || '—'}</span>
                                <span className="text-[#B8C5CE]">{req.email || '—'}</span>
                              </div>
                            </div>
                            {statusBadge(req.status)}
                          </div>

                          <div className="text-[#B8C5CE] mb-1 text-right">التخصص: {req.specialization || 'غير محدد'}</div>
                          <div className="text-[#B8C5CE] mb-1 text-right">الكلية: {req.college_name || 'غير محددة'}</div>
                          {req.contact_info && (
                            <div className="text-[#B8C5CE] mb-1 text-right">بيانات التواصل: {req.contact_info}</div>
                          )}
                          <div className="text-[#B8C5CE] mb-1 text-right">بريد مُرسل الطلب: {req.requester_email || 'غير متوفر'}</div>

                          <div className="mt-3 flex flex-wrap gap-2 justify-end">
                            <button
                              type="button"
                              onClick={() => handleRejectRequest(req)}
                              disabled={!id || loadingReject || loadingAccept}
                              className="px-3 py-1 rounded-[999px] text-[11px] disabled:opacity-60"
                              style={{
                                border: '0.75px solid rgba(248,113,113,0.8)',
                                background: 'rgba(127,29,29,0.45)',
                                color: '#FEE2E2',
                              }}
                              title={!id ? 'الطلب بدون رقم (id)' : 'رفض الطلب'}
                            >
                              {loadingReject ? 'جارٍ الرفض...' : 'رفض'}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleEditRequest(req)}
                              disabled={!id || loadingReject || loadingAccept}
                              className="px-3 py-1 rounded-[999px] text-[11px] disabled:opacity-60"
                              style={{
                                border: '0.75px solid rgba(251,191,36,0.8)',
                                background: 'rgba(120,53,15,0.4)',
                                color: '#FEF3C7',
                              }}
                              title={!id ? 'الطلب بدون رقم (id)' : 'تعديل قبل القبول'}
                            >
                              تعديل قبل القبول
                            </button>

                            <button
                              type="button"
                              onClick={() => handleAcceptRequest(req)}
                              disabled={!id || loadingAccept || loadingReject}
                              className="px-3 py-1 rounded-[999px] text-[11px] disabled:opacity-60"
                              style={{
                                border: '0.75px solid rgba(45,212,191,0.9)',
                                background:
                                  'linear-gradient(135deg,rgba(45,212,191,0.9),rgba(56,189,248,0.9))',
                                color: '#022c22',
                                boxShadow: '0 0 12px rgba(45,212,191,.45)',
                              }}
                              title={!id ? 'الطلب بدون رقم (id)' : 'قبول كما هو'}
                            >
                              {loadingAccept ? 'جارٍ القبول...' : 'قبول كما هو'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {resolvedRequests.length > 0 && (
                <div style={{ direction: 'rtl' }}>
                  <div className="mb-2 text-[12px] text-slate-200 font-medium text-right">
                    السجل (طلبات مقبولة / مرفوضة)
                  </div>
                  <div className="space-y-3">
                    {resolvedRequests.map((req) => (
                      <div
                        key={req?.id || `${req.lecturer_name}-${req.email}`}
                        className="px-3 py-3 text-[12px]"
                        style={{ background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.06) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.85)', border: '1px solid rgba(148,163,184,.35)', borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,.2)' }}
                      >
                        <div className="flex justify-between items-center mb-1 gap-2">
                          <div className="flex items-center gap-3">
                            {req.image_data && (
                              <img
                                src={req.image_data}
                                alt={req.lecturer_name || 'صورة المحاضر'}
                                className="w-9 h-9 rounded-full object-cover border border-[rgba(148,163,184,.9)]"
                              />
                            )}
                            <div className="flex flex-col items-end">
                              <span className="text-white font-semibold">{req.lecturer_name || '—'}</span>
                              <span className="text-[#B8C5CE]">{req.email || '—'}</span>
                            </div>
                          </div>
                          {statusBadge(req.status)}
                        </div>

                        <div className="text-[#B8C5CE] mb-1 text-right">التخصص: {req.specialization || 'غير محدد'}</div>
                        <div className="text-[#B8C5CE] mb-1 text-right">الكلية: {req.college_name || 'غير محددة'}</div>
                        {req.contact_info && <div className="text-[#B8C5CE] mb-1 text-right">بيانات التواصل: {req.contact_info}</div>}
                        <div className="text-[#B8C5CE] mb-1 text-right">بريد مُرسل الطلب: {req.requester_email || 'غير متوفر'}</div>

                        <div className="text-[11px] text-slate-400 text-right mt-1">
                          تم إنشاء الطلب في:{' '}
                          {req.created_at ? new Date(req.created_at).toLocaleString('ar-SA') : 'غير متوفر'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <MobileBottomNav />
      </div>
    </div>
  );
};

export default AddCourseLecturerPage;
